import { ArchiveError, type ArchiveErrorCode } from './errors'
import type { PhotoRef } from './imageIndex'
import { decodeLzmaHeader } from './lzmaHeader'
import type { PointBuilder } from './openArchive'
import { decodePoints, pointsFromGeojson, type PointColumns } from './pointColumns'
import type { HeaderDecoder } from './sevenZip'

export const WORKER_TIMEOUT_MS = 120_000

type WorkerResult = { id: number; bytes?: Uint8Array; columns?: PointColumns; code?: ArchiveErrorCode }
type Job = { resolve: (value: WorkerResult) => void; reject: (error: Error) => void }

let worker: Worker | null = null
let nextId = 0
const pending = new Map<number, Job>()

export type WorkerFactory = () => Worker
let createWorker: WorkerFactory = () => new Worker(new URL('./archiveWorker.ts', import.meta.url), { type: 'module' })

export const setWorkerFactory = (factory: WorkerFactory) => {
  resetWorker()
  createWorker = factory
}

const resetWorker = () => {
  const current = worker
  worker = null
  current?.terminate()
  const jobs = [...pending.values()]
  pending.clear()
  jobs.forEach(job => job.reject(new ArchiveError('worker-failed')))
}

const getWorker = (): Worker | null => {
  if (worker) return worker
  if (typeof Worker === 'undefined') return null
  try {
    worker = createWorker()
  } catch {
    return null
  }
  worker.onmessage = (event: MessageEvent<WorkerResult>) => {
    const job = pending.get(event.data.id)
    if (!job) return
    pending.delete(event.data.id)
    job.resolve(event.data)
  }
  worker.onerror = event => {
    event.preventDefault()
    resetWorker()
  }
  worker.onmessageerror = () => resetWorker()
  return worker
}

const callWorker = (
  current: Worker,
  payload: { id: number } & Record<string, unknown>,
  transfer: Transferable[]
): Promise<WorkerResult> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (pending.has(payload.id)) resetWorker()
    }, WORKER_TIMEOUT_MS)
    pending.set(payload.id, {
      resolve: value => {
        clearTimeout(timer)
        resolve(value)
      },
      reject: error => {
        clearTimeout(timer)
        reject(error)
      },
    })
    current.postMessage(payload, transfer)
  })

export const decodeHeaderOffThread: HeaderDecoder = async (props, packed, unpackedSize) => {
  const current = getWorker()
  if (!current) return decodeLzmaHeader(props, packed, unpackedSize)
  const result = await callWorker(current, { id: ++nextId, kind: 'lzma', props, packed, unpackedSize }, [])
  if (result.code) throw new ArchiveError(result.code)
  if (!result.bytes) throw new ArchiveError('worker-failed')
  return result.bytes
}

export const buildPointsOffThread: PointBuilder = async (bytes: Uint8Array, images: PhotoRef[]) => {
  const current = getWorker()
  if (!current) return pointsFromGeojson(bytes, images)
  const result = await callWorker(current, { id: ++nextId, kind: 'points', bytes, images }, [
    bytes.buffer as ArrayBuffer,
  ])
  if (result.code) throw new ArchiveError(result.code)
  if (!result.columns) throw new ArchiveError('worker-failed')
  return decodePoints(result.columns, images)
}
